import React, { useState } from 'react';
import { Box, Typography, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Avatar, Button, TableSortLabel, styled, Grid2, Grid, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';
import Asset360Modal from './Asset360Modal';
import RoleBasedAccess from '../../services/RoleBasedAccess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFilesAsync } from '../../services/FileDownloader';
import { useAssetsContext } from '../../context/AssetsContext';
import { useAssetTableLogic } from '../../hooks/assets/useAssetTableLogic';
import AssetStatusTag from './AssetStatusTag';

const AssetDesktopTable: React.FC = () => {
  const navigate = useNavigate();
  const { assets, sortDirection, setSortDirection } = useAssetsContext();
  const {
    openModal,
    modalAsset,
    thumbnailErrors,
    handleThumbnailError,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    handleSelectAllClick,
    buildAssetDetailPath,
    buildAssetNavState,
    isSelected,
    isAllSelected,
    isIndeterminate,
  } = useAssetTableLogic();

  const SpinningButton = styled(Button)(({ theme }) => ({
    borderRadius: '5px',
    padding: '12px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.warning.main,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 4px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  }));

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <RoleBasedAccess allowedRoles={["Internal", "Client Plus"]}>
                <TableCell padding="checkbox">
                  <Checkbox
                    onClick={handleSelectAllClick}
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: (theme) => theme.palette.status.accent,
                      },
                    }}
                  />
                </TableCell>
              </RoleBasedAccess>
              <TableCell>ID</TableCell>
              <TableCell>Image Counts</TableCell>
              <TableCell>Processing</TableCell>
              <TableCell>Syndication</TableCell>
              <TableCell>
                <TableSortLabel
                  active
                  direction={sortDirection}
                  onClick={() =>
                    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
                  }
                >
                  Post Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Config</TableCell>
              <TableCell>360</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => (
              <TableRow
                key={asset.assetInstanceId}
                hover
              >
                <RoleBasedAccess allowedRoles={["Internal", "Client Plus"]}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      onClick={(e) => handleCheckboxClick(e, asset)}
                      checked={isSelected(asset)}
                      size="small"
                      sx={{
                        '&.Mui-checked': {
                          color: (theme) => theme.palette.status.accent,
                        },
                      }}
                    />
                  </TableCell>
                </RoleBasedAccess>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <Box display="flex" alignItems="center" flexDirection="column" justifyContent="center" gap={0.25}>
                      <Link
                        component={RouterLink}
                        to={buildAssetDetailPath(asset)}
                        state={buildAssetNavState(asset)}
                        underline="hover"
                        sx={{
                          color: "inherit",
                          fontWeight: 500,
                          textAlign: "center",
                          marginBottom: 0.5,
                          wordBreak: "break-word",
                          cursor: "pointer",
                          fontSize: "1rem",
                        }}
                      >
                        {asset.assetId}
                      </Link>

                      {asset.archived ? (
                        <Box
                          sx={{
                            height: 120,
                            width: 200,
                            backgroundColor: '#ffffff',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              color: 'black',
                              textAlign: 'center',
                            }}
                          >
                            Archived Asset
                          </Typography>
                        </Box>
                      ) : asset.thumbnail && !thumbnailErrors[asset.assetInstanceId] ? (
                        <Box
                          onClick={() => navigate(buildAssetDetailPath(asset), { state: buildAssetNavState(asset) })}
                          sx={{
                            cursor: 'pointer',
                            '&:hover img': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Avatar
                            variant="square"
                            src={asset.thumbnail}
                            onError={() => handleThumbnailError(asset.assetInstanceId)}
                            sx={{ height: 120, width: 200, borderRadius: 3 }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            height: 100,
                            width: 180,
                            backgroundColor: '#ffffff',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              color: 'black',
                              textAlign: 'center',
                            }}
                          >
                            No Thumbnail
                          </Typography>
                        </Box>
                      )}
                    </Box>


                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box display="flex" flexDirection="column" justifyContent="space-between" height="140px">
                        <Tooltip title="Copy Asset ID">
                          <IconButton
                            component="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigator.clipboard.writeText(asset.assetId);
                            }}
                            sx={{
                              padding: 0.25,            // make button tighter
                              color: (theme) => theme.palette.grey[400], // light grey
                              '&:hover': {
                                color: (theme) => theme.palette.grey[600], // slightly darker on hover
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} /> {/* super small */}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Open in New Tab">
                          <IconButton
                            component="a"
                            href={buildAssetDetailPath(asset)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} // ✅ Prevents row click but allows new tab
                            sx={{
                              padding: 0.25,
                              color: (theme) => theme.palette.grey[400],
                              "&:hover": {
                                color: (theme) => theme.palette.grey[600],
                              },
                            }}
                          >
                            <OpenInNewIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Images">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFilesAsync(asset);
                            }}
                            sx={{
                              padding: 0.25,
                              color: (theme) => theme.palette.grey[400],
                              "&:hover": {
                                color: (theme) => theme.palette.grey[600],
                              },
                            }}
                          >
                            <DownloadIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell sx={{ px: 0 }}>
                  {[
                    { key: "imageExterior", label: "Exterior", images: asset.imageExterior || [] },
                    { key: "imageCrop", label: "Cropped", images: asset.imageCrop || [] },
                    { key: "imageFeatured", label: "Featured", images: asset.imageFeatured || [] },
                    { key: "imageManual", label: "Manual", images: asset.imageManual || [] },
                    { key: "imagePano", label: "Panoramic", images: asset.imagePano || [] },
                    { key: "imageUnderCarriage", label: "UC", images: asset.imageUndercarriage || [] },
                    { key: "imageBgr", label: "BGR", images: asset.imageBgr || [] },
                  ].map(({ key, label, images }) => (
                    <Grid2 container key={key} sx={{ gap: 0.5, marginRight: 6 }}>
                      <Grid2 size={{ xs: 12, sm: 10 }} sx={{ alignItems: 'end', p: 0, width: 'fit-content' }}>
                        <Typography sx={{ textAlign: 'end' }}>
                          {`${label}:`}
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 1 }}>
                        <Typography>
                          {images.length}
                        </Typography>
                      </Grid2>
                    </Grid2>
                  ))}
                </TableCell>

                <TableCell>
                    <AssetStatusTag
                    flagValue={asset.processingProcessFlagValue}
                    title={asset.processingError || undefined}
                  >
                    {asset.processingCurrentStep}
                  </AssetStatusTag>
                </TableCell>
                <TableCell>
                  <AssetStatusTag
                    flagValue={asset.syndicationProcessFlagValue}
                    title={asset.syndicationError || undefined}
                  >
                    {asset.syndicationCurrentStep}
                  </AssetStatusTag>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(+asset.created_at * 1000).toLocaleString([], {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>{asset.config}</TableCell>
                <TableCell>
                  {asset.imagePano?.length ? (
                    <SpinningButton onClick={(e: React.MouseEvent) => handle360Click(e, asset.systemCode, asset.assetInstanceId)}>
                      360
                      <SyncTwoToneIcon sx={{ fontSize: '18px', marginLeft: '5px' }} />
                    </SpinningButton>
                  ) : (
                    <Typography>No Panos</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Asset 360 Modal */}
      <Asset360Modal
        open={openModal}
        handleClose={handleCloseModal}
        systemCode={modalAsset?.systemCode || null}
        assetInstanceId={modalAsset?.assetInstanceId || null}
      />
    </>
  );
};

export default AssetDesktopTable;
